import { useTranslation } from "react-i18next";
import { Loader2, Trash2, Clock } from "lucide-react";
import { translateError } from "@/lib/errors";
import { StatusPill, type StatusTone } from "@/components/primitives/StatusPill";
import { fmtDuration } from "@/lib/montageApi";
import type { MusicJob } from "@/lib/musicApi";

const TONE: Record<MusicJob["status"], StatusTone> = { PROCESSING: "cyan", READY: "success", ERRORE: "danger" };
const LABEL: Record<MusicJob["status"], string> = {
  PROCESSING: "music.statusProcessing",
  READY: "music.statusReady",
  ERRORE: "common.error",
};

interface Props {
  jobs: MusicJob[];
  selectedId: string | null;
  onSelect: (job: MusicJob) => void;
  onDelete: (job: MusicJob) => void;
}

// Compact history of the user's MP4s (latest first). Selecting a READY job shows it in the result card.
export function MusicJobList({ jobs, selectedId, onSelect, onDelete }: Props) {
  const { t } = useTranslation();
  if (jobs.length === 0) return null;
  return (
    <section data-testid="music-job-list" className="mt-10">
      <p className="mb-3 font-display text-[11px] font-semibold uppercase tracking-[0.26em] text-[#FF7AD5]">{t("music.history")}</p>
      <ul className="space-y-2">
        {jobs.map((job) => {
          const selected = job.id === selectedId;
          return (
            <li key={job.id}>
              <div
                data-testid={`music-job-${job.id}`}
                className={
                  "flex items-center gap-3 rounded-xl border px-4 py-3 transition-[border-color,background-color] duration-200 " +
                  (selected ? "border-[#FF3DBE]/60 bg-[#12061A]/70" : "border-[#FF3DBE]/15 bg-[#05070D]/50 hover:border-[#FF3DBE]/40")
                }
              >
                <button
                  type="button"
                  data-testid={`music-job-select-${job.id}`}
                  onClick={() => onSelect(job)}
                  disabled={job.status !== "READY"}
                  className="min-w-0 flex-1 text-left disabled:cursor-default"
                >
                  <span className="block truncate text-sm font-medium">{job.image_name.replace(/\.[^.]+$/, "") || "MP4"}</span>
                  <span className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" /> {fmtDuration(job.duration)} · {job.aspect_ratio}
                    {job.status === "ERRORE" && job.error && (
                      <span data-testid={`music-job-error-${job.id}`} className="truncate text-[#FF5C6C]">· {translateError(job.error)}</span>
                    )}
                  </span>
                </button>
                <StatusPill tone={TONE[job.status]} pulse={job.status === "PROCESSING"}>
                  {job.status === "PROCESSING" && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}
                  {t(LABEL[job.status])}
                </StatusPill>
                <button
                  type="button"
                  data-testid={`music-job-delete-${job.id}`}
                  title={t("common.delete")}
                  onClick={() => onDelete(job)}
                  disabled={job.status === "PROCESSING"}
                  className="rounded-md p-1.5 text-muted-foreground transition-colors hover:text-[#FF5C6C] disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
